import React, { useState, useEffect, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { teamsAPI } from '../api/client';
import {
  ChatBubbleLeftRightIcon, ArrowPathIcon, TrashIcon,
  ExclamationTriangleIcon, SparklesIcon, ArrowTopRightOnSquareIcon,
  CheckIcon, InboxIcon, EnvelopeIcon, BoltIcon, CheckCircleIcon,
  ChevronDownIcon, ChevronRightIcon, ArrowUturnLeftIcon, XMarkIcon,
  NoSymbolIcon,
} from '@heroicons/react/24/outline';

const PRIORITY = {
  high:   { cls: 'bg-red-100 text-red-700',     dot: 'bg-red-500',    label: 'High',   rank: 0 },
  medium: { cls: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500',  label: 'Medium', rank: 1 },
  low:    { cls: 'bg-slate-100 text-slate-500', dot: 'bg-slate-400',  label: 'Low',    rank: 2 },
};

const FILTER_TABS = [
  { key: 'all',       label: 'All' },
  { key: 'pending',   label: 'Pending Actions' },
  { key: 'completed', label: 'Completed' },
  { key: 'ignored',   label: 'Ignored' },
];

const GROUP_OPTIONS = [
  { key: 'none',     label: 'No grouping' },
  { key: 'person',   label: 'Person' },
  { key: 'channel',  label: 'Channel' },
  { key: 'priority', label: 'Priority' },
];

const fmtWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const whereLabel = (m) =>
  m.source_type === 'channel'
    ? `#${m.channel_name || 'channel'} · ${m.team_name || ''}`
    : (m.chat_topic || 'Direct chat');

const isThisWeek = (iso) => {
  if (!iso) return false;
  return Date.now() - new Date(iso).getTime() <= 7 * 24 * 60 * 60 * 1000;
};

export default function Teams() {
  const [conn, setConn] = useState(null);
  const [mentions, setMentions] = useState([]);
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [busy, setBusy] = useState({});

  // Feature 1 — action tracker
  const [filterTab, setFilterTab] = useState('all');
  // Feature 3 — grouping + quick chips
  const [groupBy, setGroupBy] = useState('none');
  const [activeChip, setActiveChip] = useState(null);   // { type: 'person'|'channel', value }
  const [collapsed, setCollapsed] = useState({});        // group key → true when collapsed
  // Feature 2 — reply
  const [replyOpen, setReplyOpen] = useState(null);      // mention id
  const [replyText, setReplyText] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);
  const [replySaving, setReplySaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, m, d] = await Promise.all([
        teamsAPI.getConnection(),
        teamsAPI.listMentions(),
        teamsAPI.latestDigest(),
      ]);
      setConn(c.data);
      setMentions(m.data);
      setDigest(d.data || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load Teams data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const refreshConn = async () => {
    try { setConn((await teamsAPI.getConnection()).data); } catch { /* ignore */ }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const res = await teamsAPI.sync();
      toast.success(res.data.message);
      const m = await teamsAPI.listMentions();
      setMentions(m.data);
      refreshConn();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed', { duration: 6000 });
    } finally {
      setSyncing(false);
    }
  };

  const patchLocal = (id, changes) =>
    setMentions((prev) => prev.map((x) => (x.id === id ? { ...x, ...changes } : x)));

  const markRead = async (m, isRead) => {
    setBusy((b) => ({ ...b, [m.id]: true }));
    try {
      await teamsAPI.markRead(m.id, isRead);
      patchLocal(m.id, { is_read: isRead });
      refreshConn();
    } catch { toast.error('Failed to update'); }
    finally { setBusy((b) => ({ ...b, [m.id]: false })); }
  };

  const markAllRead = async () => {
    try {
      const res = await teamsAPI.markAllRead();
      toast.success(`Marked ${res.data.marked_read} as read`);
      setMentions((prev) => prev.map((x) => ({ ...x, is_read: true })));
      refreshConn();
    } catch { toast.error('Failed to mark all read'); }
  };

  const setStatus = async (m, status) => {
    setBusy((b) => ({ ...b, [m.id]: true }));
    try {
      const res = await teamsAPI.updateStatus(m.id, status);
      patchLocal(m.id, { status: res.data.status, status_updated_at: res.data.status_updated_at });
    } catch { toast.error('Failed to update status'); }
    finally { setBusy((b) => ({ ...b, [m.id]: false })); }
  };

  const removeMention = async (m) => {
    setBusy((b) => ({ ...b, [m.id]: true }));
    try {
      await teamsAPI.deleteMention(m.id);
      setMentions((prev) => prev.filter((x) => x.id !== m.id));
      refreshConn();
    } catch { toast.error('Failed to delete'); }
    finally { setBusy((b) => ({ ...b, [m.id]: false })); }
  };

  const generateDigest = async () => {
    setGenerating(true);
    try {
      const res = await teamsAPI.generateDigest();
      setDigest(res.data);
      toast.success(res.data.emailed ? 'Digest generated & emailed' : 'Digest generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate digest');
    } finally { setGenerating(false); }
  };

  // ── Reply flow ──────────────────────────────────────────────────────────
  const openReply = async (m) => {
    if (replyOpen === m.id) { setReplyOpen(null); return; }
    setReplyOpen(m.id);
    setReplyText(m.reply_text || '');
    if (!m.reply_text) {
      setReplyLoading(true);
      try {
        const res = await teamsAPI.generateReply(m.id);
        setReplyText(res.data.reply || '');
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Could not draft a reply');
      } finally { setReplyLoading(false); }
    }
  };

  const copyAndOpen = async (m) => {
    const text = replyText.trim();
    if (!text) { toast.error('Reply is empty'); return; }
    setReplySaving(true);
    try {
      const res = await teamsAPI.saveReply(m.id, text);
      patchLocal(m.id, { reply_text: res.data.reply_text, reply_sent_at: res.data.reply_sent_at });
      const tail = m.web_url ? ' — opening Teams' : '';
      try { await navigator.clipboard.writeText(text); toast.success(`Reply copied${tail}`); }
      catch { toast.success(`Reply saved${tail}`); }
      if (m.web_url) window.open(m.web_url, '_blank', 'noreferrer');
      setReplyOpen(null);
    } catch {
      toast.error('Failed to save reply');
    } finally { setReplySaving(false); }
  };

  // ── Derived: summary, chips, filtering, grouping ─────────────────────────
  const pendingCount = useMemo(() => mentions.filter((m) => (m.status || 'pending') === 'pending').length, [mentions]);
  const completedThisWeek = useMemo(
    () => mentions.filter((m) => m.status === 'done' && isThisWeek(m.status_updated_at)).length,
    [mentions],
  );

  const topChips = useMemo(() => {
    const countBy = (keyFn) => {
      const map = new Map();
      mentions.forEach((m) => { const k = keyFn(m); if (k) map.set(k, (map.get(k) || 0) + 1); });
      return [...map.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5);
    };
    return {
      people: countBy((m) => m.from_name),
      channels: countBy((m) => whereLabel(m)),
    };
  }, [mentions]);

  const filtered = useMemo(() => {
    let list = mentions;
    if (filterTab === 'pending') list = list.filter((m) => (m.status || 'pending') === 'pending');
    else if (filterTab === 'completed') list = list.filter((m) => m.status === 'done');
    else if (filterTab === 'ignored') list = list.filter((m) => m.status === 'ignored');
    if (activeChip) {
      list = list.filter((m) =>
        activeChip.type === 'person' ? m.from_name === activeChip.value : whereLabel(m) === activeChip.value);
    }
    return list;
  }, [mentions, filterTab, activeChip]);

  const groups = useMemo(() => {
    if (groupBy === 'none') return null;
    const keyFn =
      groupBy === 'person'  ? (m) => m.from_name || 'Unknown'
      : groupBy === 'channel' ? (m) => whereLabel(m)
      : (m) => m.ai_priority || 'medium';
    const map = new Map();
    filtered.forEach((m) => { const k = keyFn(m); if (!map.has(k)) map.set(k, []); map.get(k).push(m); });
    let entries = [...map.entries()];
    if (groupBy === 'priority') entries.sort((a, b) => (PRIORITY[a[0]]?.rank ?? 1) - (PRIORITY[b[0]]?.rank ?? 1));
    else entries.sort((a, b) => b[1].length - a[1].length);
    return entries;
  }, [filtered, groupBy]);

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  const notReady = !conn?.azure_configured || !conn?.teams_upn || !conn?.enabled;

  return (
    <div className="max-w-3xl mx-auto px-3 sm:px-0 space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #5b5fc7 100%)' }}>
            <ChatBubbleLeftRightIcon className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Teams Mentions</h1>
            <p className="text-sm text-slate-500 mt-0.5 truncate">
              {conn?.enabled
                ? `Monitoring ${conn.display_name || conn.teams_upn || '—'}`
                : 'Monitoring is off'}
            </p>
          </div>
        </div>
        <button
          onClick={sync}
          disabled={syncing || !conn?.teams_upn || !conn?.azure_configured}
          className="flex items-center gap-1.5 px-3 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors"
        >
          <ArrowPathIcon className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Checking…' : 'Sync Now'}
        </button>
      </div>

      {/* Summary bar (action tracker) */}
      <div className="flex items-center gap-4 rounded-xl bg-white border border-slate-200 px-4 py-3 text-sm shadow-card">
        <span className="flex items-center gap-1.5 font-semibold text-slate-800">
          <span className="w-2 h-2 rounded-full bg-amber-500" /> {pendingCount} pending action{pendingCount !== 1 ? 's' : ''}
        </span>
        <span className="text-slate-300">·</span>
        <span className="flex items-center gap-1.5 text-slate-500">
          <CheckCircleIcon className="w-4 h-4 text-green-500" /> {completedThisWeek} completed this week
        </span>
      </div>

      {/* Not-configured banner */}
      {notReady && (
        <div className="flex items-start gap-2 p-4 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-800">
          <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">
              {!conn?.azure_configured ? 'Azure app credentials are not configured.'
                : !conn?.teams_upn ? 'No account is being monitored yet.'
                : 'Teams monitoring is turned off.'}
            </p>
            <p className="text-xs mt-0.5">An administrator can configure this in <strong>Admin → Integrations → Teams</strong>.</p>
          </div>
        </div>
      )}

      {conn?.last_error && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-100 text-xs text-red-600">
          <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span className="break-words">Last sync error: {conn.last_error}</span>
        </div>
      )}

      {/* Daily digest card */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-card overflow-hidden">
        <div className="p-4 flex items-center justify-between gap-2 border-b border-slate-100">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-violet-50 flex-shrink-0">
              <SparklesIcon className="w-4 h-4 text-violet-600" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-slate-800 text-sm">Daily Digest</p>
              <p className="text-xs text-slate-500 truncate">
                {digest ? `${digest.digest_date} · ${digest.mention_count} mention(s)` : 'No digest yet'}
                {digest?.emailed && <span className="ml-1 text-green-600">· emailed</span>}
              </p>
            </div>
          </div>
          <button
            onClick={generateDigest}
            disabled={generating}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-violet-600 border border-violet-200 rounded-lg hover:bg-violet-50 disabled:opacity-50 transition-colors flex-shrink-0"
          >
            <BoltIcon className="w-3.5 h-3.5" /> {generating ? 'Generating…' : 'Generate now'}
          </button>
        </div>
        {digest?.summary && (
          <div className="p-4">
            <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">{digest.summary}</pre>
          </div>
        )}
      </div>

      {/* Toolbar: filter tabs + grouping + chips */}
      <div className="space-y-2.5">
        <div className="flex flex-wrap items-center gap-2 justify-between">
          {/* Filter tabs */}
          <div className="flex flex-wrap gap-1.5">
            {FILTER_TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setFilterTab(t.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                  filterTab === t.key ? 'bg-primary-50 text-primary-700 border-primary-200'
                    : 'text-slate-500 border-slate-200 hover:bg-slate-50'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Group-by: segmented on desktop, dropdown on mobile */}
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
              {GROUP_OPTIONS.map((g) => (
                <button
                  key={g.key}
                  onClick={() => setGroupBy(g.key)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                    groupBy === g.key ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {g.key === 'none' ? 'Group' : g.label}
                </button>
              ))}
            </div>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              className="sm:hidden text-xs border border-slate-200 rounded-lg px-2 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
            >
              {GROUP_OPTIONS.map((g) => <option key={g.key} value={g.key}>Group by: {g.label}</option>)}
            </select>
            {conn?.unread_count > 0 && (
              <button
                onClick={markAllRead}
                className="flex items-center gap-1 text-xs px-2.5 py-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 transition-colors"
              >
                <CheckIcon className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Mark all read</span>
              </button>
            )}
          </div>
        </div>

        {/* Quick filter chips: top senders + channels */}
        {(topChips.people.length > 0 || topChips.channels.length > 0) && (
          <div className="flex flex-wrap gap-1.5">
            {activeChip && (
              <button
                onClick={() => setActiveChip(null)}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-slate-800 text-white"
              >
                <XMarkIcon className="w-3 h-3" /> Clear filter
              </button>
            )}
            {topChips.people.map(([name, n]) => (
              <Chip key={`p-${name}`} active={activeChip?.type === 'person' && activeChip.value === name}
                onClick={() => setActiveChip((c) => (c?.type === 'person' && c.value === name ? null : { type: 'person', value: name }))}>
                {name} <span className="opacity-60">{n}</span>
              </Chip>
            ))}
            {topChips.channels.map(([name, n]) => (
              <Chip key={`c-${name}`} active={activeChip?.type === 'channel' && activeChip.value === name}
                onClick={() => setActiveChip((c) => (c?.type === 'channel' && c.value === name ? null : { type: 'channel', value: name }))}>
                {name} <span className="opacity-60">{n}</span>
              </Chip>
            ))}
          </div>
        )}
      </div>

      {/* Mentions feed */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-slate-200 text-slate-400">
          <InboxIcon className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="font-medium text-slate-600">No mentions here</p>
          <p className="text-sm mt-1">
            {mentions.length === 0
              ? (conn?.enabled ? 'New @mentions appear after the next sync (every 5 min).' : 'Ask an admin to enable monitoring, then Sync Now.')
              : 'Try a different filter or clear the active chip.'}
          </p>
        </div>
      ) : groups ? (
        <div className="space-y-3">
          {groups.map(([key, items]) => {
            const isCollapsed = !!collapsed[key];
            const label = groupBy === 'priority' ? (PRIORITY[key]?.label || key) : key;
            return (
              <div key={key} className="bg-white rounded-2xl border border-slate-200 shadow-card overflow-hidden">
                <button
                  onClick={() => setCollapsed((c) => ({ ...c, [key]: !c[key] }))}
                  className="w-full flex items-center justify-between gap-2 px-4 py-3 hover:bg-slate-50"
                >
                  <span className="flex items-center gap-2 min-w-0">
                    {isCollapsed ? <ChevronRightIcon className="w-4 h-4 text-slate-400" /> : <ChevronDownIcon className="w-4 h-4 text-slate-400" />}
                    <span className="font-semibold text-slate-800 text-sm truncate">{label}</span>
                  </span>
                  <span className="text-xs text-slate-400 flex-shrink-0">{items.length}</span>
                </button>
                {!isCollapsed && (
                  <div className="p-3 pt-0 space-y-3">
                    {items.map((m) => (
                      <MentionCard key={m.id} m={m} busy={busy} onMarkRead={markRead} onSetStatus={setStatus}
                        onDelete={removeMention} onReply={openReply}
                        replyOpen={replyOpen} replyText={replyText} setReplyText={setReplyText}
                        replyLoading={replyLoading} replySaving={replySaving} onCopyOpen={copyAndOpen} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((m) => (
            <MentionCard key={m.id} m={m} busy={busy} onMarkRead={markRead} onSetStatus={setStatus}
              onDelete={removeMention} onReply={openReply}
              replyOpen={replyOpen} replyText={replyText} setReplyText={setReplyText}
              replyLoading={replyLoading} replySaving={replySaving} onCopyOpen={copyAndOpen} />
          ))}
        </div>
      )}
    </div>
  );
}

function Chip({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border transition-colors max-w-full truncate ${
        active ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
      }`}
    >
      {children}
    </button>
  );
}

function MentionCard({
  m, busy, onMarkRead, onSetStatus, onDelete, onReply,
  replyOpen, replyText, setReplyText, replyLoading, replySaving, onCopyOpen,
}) {
  const pri = PRIORITY[m.ai_priority] || PRIORITY.medium;
  const status = m.status || 'pending';
  const highlight = status === 'pending' && !!m.ai_action;   // pending w/ action → left border
  const replied = !!m.reply_sent_at;
  const isReplyOpen = replyOpen === m.id;

  return (
    <div
      className={`bg-white rounded-2xl border p-4 shadow-card transition-colors ${
        status === 'ignored' ? 'opacity-60' : ''
      } ${highlight ? 'border-l-4 border-l-violet-400 border-y-slate-200 border-r-slate-200'
        : m.is_read ? 'border-slate-200' : 'border-primary-200 ring-1 ring-primary-100'}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            {!m.is_read && <span className="w-2 h-2 rounded-full bg-primary-500 flex-shrink-0" />}
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${pri.cls}`}>{pri.label}</span>
            <span className="text-sm font-semibold text-slate-800 truncate">{m.from_name || 'Someone'}</span>
            <span className="text-xs text-slate-400 truncate">in {whereLabel(m)}</span>
            <span className="text-xs text-slate-300">· {fmtWhen(m.message_created_at)}</span>
            {status === 'done' && <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700">Done</span>}
            {status === 'ignored' && <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-slate-200 text-slate-500">Ignored</span>}
            {replied && <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-sky-100 text-sky-700">Replied</span>}
          </div>

          {m.ai_summary && <p className="text-sm text-slate-700 mt-1">{m.ai_summary}</p>}

          {m.ai_action && (
            <div className="flex items-start gap-1.5 mt-2 px-2.5 py-1.5 rounded-lg bg-violet-50 border border-violet-100">
              <SparklesIcon className="w-3.5 h-3.5 text-violet-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-violet-700"><span className="font-semibold">Suggested action:</span> {m.ai_action}</p>
            </div>
          )}

          {m.message_text && (
            <details className="mt-2">
              <summary className="text-xs text-slate-400 cursor-pointer hover:text-slate-600 select-none">Original message</summary>
              <p className="text-xs text-slate-500 mt-1 whitespace-pre-wrap border-l-2 border-slate-100 pl-2">{m.message_text}</p>
            </details>
          )}

          {/* Reply editor */}
          {isReplyOpen && (
            <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-2.5">
              {replyLoading ? (
                <div className="flex items-center gap-2 text-xs text-slate-500 py-3 px-1">
                  <span className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-primary-500" /> Drafting reply…
                </div>
              ) : (
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  rows={4}
                  placeholder="Type your reply…"
                  className="w-full text-sm border border-slate-200 rounded-lg px-2.5 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white resize-y"
                />
              )}
              <div className="flex items-center justify-between gap-2 mt-2">
                <p className="text-[11px] text-slate-400">
                  {m.web_url ? 'Copies your reply and opens Teams to paste & send.' : 'Copies your reply to paste in Teams (link unavailable).'}
                </p>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={() => onReply(m)} className="text-xs px-2.5 py-1.5 rounded-lg text-slate-500 hover:bg-slate-100">Cancel</button>
                  <button
                    onClick={() => onCopyOpen(m)}
                    disabled={replyLoading || replySaving || !replyText.trim()}
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                  >
                    <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                    {replySaving ? 'Saving…' : m.web_url ? 'Copy & Open in Teams' : 'Copy reply'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Action row */}
          <div className="flex items-center gap-x-4 gap-y-2 mt-2.5 flex-wrap">
            <button
              onClick={() => onSetStatus(m, status === 'done' ? 'pending' : 'done')}
              disabled={busy[m.id]}
              className={`inline-flex items-center gap-1 text-xs disabled:opacity-50 ${status === 'done' ? 'text-green-600 font-medium' : 'text-slate-500 hover:text-green-600'}`}
            >
              <CheckCircleIcon className="w-4 h-4" /> {status === 'done' ? 'Done' : 'Mark done'}
            </button>
            <button
              onClick={() => onSetStatus(m, status === 'ignored' ? 'pending' : 'ignored')}
              disabled={busy[m.id]}
              className={`inline-flex items-center gap-1 text-xs disabled:opacity-50 ${status === 'ignored' ? 'text-slate-600 font-medium' : 'text-slate-500 hover:text-slate-700'}`}
            >
              <NoSymbolIcon className="w-4 h-4" /> {status === 'ignored' ? 'Ignored' : 'Ignore'}
            </button>
            <button
              onClick={() => onReply(m)}
              className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
            >
              <ArrowUturnLeftIcon className="w-4 h-4" /> Reply
            </button>
            {m.web_url && (
              <a href={m.web_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
                <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" /> Open in Teams
              </a>
            )}
            <button
              onClick={() => onMarkRead(m, !m.is_read)}
              disabled={busy[m.id]}
              className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 disabled:opacity-50"
            >
              {m.is_read ? <EnvelopeIcon className="w-3.5 h-3.5" /> : <CheckIcon className="w-3.5 h-3.5" />}
              {m.is_read ? 'Mark unread' : 'Mark read'}
            </button>
          </div>
        </div>

        <button
          onClick={() => onDelete(m)}
          disabled={busy[m.id]}
          className="p-2 text-slate-300 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors flex-shrink-0"
          title="Delete"
        >
          <TrashIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
